import React, { useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { get, isEmpty } from 'lodash';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import EsmsDialog from '../../components/EsmsDialog';
import GoogleLogo from '../../components/GoogleLogo';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';
import TimeZoneAutoSelect from '../../components/TimeZoneAutoSelect';
import { squareAreaUOMTypes } from '../../data/areaTypes';

const ValidationSchema = Yup.object().shape({
    clientId: Yup.string()
        .max(45)
        .required(),
    name: Yup.string()
        .max(45)
        .required(),
    buildingType: Yup.string()
        .required(),
    squareAreaUOM: Yup.string(),
    squareFootage: Yup.number()
        .typeError('Please input a number')
        .required(),
    fuelTypes:         Yup.array(),
    salesContact:      Yup.string(),
    facilitiesContact: Yup.string(),
    energyContact:     Yup.string(),
    addressSearch:     Yup.string()
        .required(),
    timeZone: Yup.string().required()
});

const CreateSiteModal = ({
    contacts,
    open,
    close,
    title,
    isLoading,
    errors,
    selectedNode,
    clearErrors,
    toggleLoading,
    buildingTypes,
    clientNodes,
    fuelTypes,
    selectedClient,
    ...props
}) => {
    const [address, setAddress] = useState({});

    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');

    const formik = useFormik({
        initialValues: {
            clientId:          selectedClient && selectedClient.id ? selectedClient.id : '',
            name:              '',
            buildingType:      '',
            squareFootage:     '',
            squareAreaUOM:     'SquareFootage',
            fuelTypes:         [],
            salesContact:      '',
            facilitiesContact: '',
            energyContact:     '',
            timeZone:          '',
            addressSearch:     ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                createArchitectSite
            } = props;

            let siteId;

            let fuelTypeIds = fuelTypes.filter(f => formik.values.fuelTypes.includes(f.name)).map(type => type.id);

            const body = {
                name:           values.name,
                buildingTypeId: values.buildingType,
                squareFootage:  values.squareFootage,
                fuelTypes:      fuelTypeIds,
                squareAreaUOM:  values.squareAreaUOM,
                timeZone:       values.timeZone.name,
                address
            };

            if (!isEmpty(values.facilitiesContact)) body.facilitiesContactId = values.facilitiesContact;
            if (!isEmpty(values.salesContact)) body.salesContactId = values.salesContact;
            if (!isEmpty(values.energyContact)) body.energyContactId = values.energyContact;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    if (selectedNode.type === 'campus') {
                        const campusId = selectedNode.id;
                        siteId = await createArchitectSite(values.clientId, companyInfo, body, campusId);
                    } else {
                        siteId = await createArchitectSite(values.clientId, companyInfo, body);
                    }
                }

                if (!isEmpty(siteId)) handleClose();
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3.long_name;

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    const handleReset = () => {
        setAddress1('');
        setCity('');
        setState('');
        setZip('');

        formik.handleReset();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            isMultiColumn={true}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/siteTreeIcon.svg" alt="new site icon" />
                        <span>New Site</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select a Client.
                        </p>
                        <p>
                            Enter a unique Site name.
                        </p>
                        <p>
                            Provide the area of your Site and unit of measure.
                        </p>
                        <p>
                            Select one more more Fuel Types(optional).
                        </p>
                        <p>
                            Select a Building Type.
                        </p>
                        <p>
                            Enter the physical rooftop address into Search for Address; we'll fill out the address fields.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="clientId"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.clientId) && Boolean(formik.touched.clientId)}
                            >
                                Select Client
                            </InputLabel>
                            <Select
                                value={formik.values.clientId}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'clientId',
                                    id:     'clientId',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.clientId) && Boolean(formik.touched.clientId)}
                            >
                                {
                                    clientNodes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.clientId &&
                             formik.errors.clientId && <FormHelperText className={classes.error}>{formik.errors.clientId}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Site Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="buildingType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.buildingType) && Boolean(formik.touched.buildingType)}
                            >
                                Building Type
                            </InputLabel>
                            <Select
                                value={formik.values.buildingType}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'buildingType',
                                    id:     'buildingType',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.buildingType) && Boolean(formik.touched.buildingType)}
                            >
                                {
                                    buildingTypes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.buildingType &&
                             formik.errors.buildingType && <FormHelperText className={classes.error}>{formik.errors.buildingType}</FormHelperText>}
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="squareFootage"
                                    name="squareFootage"
                                    type="text"
                                    value={formik.values.squareFootage}
                                    label="Area"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.squareFootage) && Boolean(formik.touched.squareFootage)}
                                    helperText={Boolean(formik.touched.squareFootage) && formik.errors.squareFootage}
                                />
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="squareAreaUOM"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    Unit Of Measure<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.squareAreaUOM}
                                    onChange={formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'squareAreaUOM',
                                        id:     'squareAreaUOM',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    {
                                        squareAreaUOMTypes.map(uom =>
                                            <MenuItem
                                                key={uom.type}
                                                value={uom.value}
                                            >
                                                {uom.type}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.squareAreaUOM &&
                                formik.errors.squareAreaUOM && <FormHelperText className={classes.error}>{formik.errors.squareAreaUOM}</FormHelperText>}
                            </FormControl>
                        </div>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="fuelTypes"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                            >
                                Fuel Types
                            </InputLabel>
                            <Select
                                multiple
                                value={formik.values.fuelTypes}
                                id="fuelTypes"
                                input={<Input id="select-multiple-checkbox" />}
                                renderValue={selected => selected.join(', ')}
                                onChange={formik.handleChange}
                                name="fuelType"
                                className={`${classes.cssUnderline} ${classes.input}`}
                                inputProps={{
                                    name: 'fuelTypes',
                                    id:   'fuelTypes',
                                }}
                            >
                                {
                                    fuelTypes.map(f => f.name).map((type, index) => (
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            value={type}
                                            key={index}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={formik.values.fuelTypes.indexOf(type) > -1}
                                            />

                                            <span>{type}</span>
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        {/*<FormControl className={classes.inputWrapper}>*/}
                        {/*    <InputLabel*/}
                        {/*        htmlFor="energyContact"*/}
                        {/*        classes={{*/}
                        {/*            error:   classes.error,*/}
                        {/*            focused: classes.labelFocused,*/}
                        {/*            root:    classes.label*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.energyContact) && Boolean(formik.touched.energyContact)}*/}
                        {/*    >*/}
                        {/*        Energy Contact*/}
                        {/*    </InputLabel>*/}
                        {/*    <Select*/}
                        {/*        value={formik.values.energyContact}*/}
                        {/*        id="energyContact"*/}
                        {/*        onChange={formik.handleChange}*/}
                        {/*        name="energyContact"*/}
                        {/*        classes={{*/}
                        {/*            root: classes.input*/}
                        {/*        }}*/}
                        {/*        inputProps={{*/}
                        {/*            onBlur: formik.handleBlur*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.energyContact) && Boolean(formik.touched.energyContact)}*/}
                        {/*    >*/}
                        {/*        {*/}
                        {/*            contacts.map(type =>*/}
                        {/*                <MenuItem*/}
                        {/*                    key={type.id}*/}
                        {/*                    value={type.id}*/}
                        {/*                >*/}
                        {/*                    {type.name}*/}
                        {/*                </MenuItem>*/}
                        {/*            )*/}
                        {/*        }*/}
                        {/*    </Select>*/}
                        {/*    {formik.touched.energyContact &&*/}
                        {/*     formik.errors.energyContact && <FormHelperText className={classes.error}>{formik.errors.energyContact}</FormHelperText>}*/}
                        {/*</FormControl>*/}
                        {/*<FormControl className={classes.inputWrapper}>*/}
                        {/*    <InputLabel*/}
                        {/*        htmlFor="facilitiesContact"*/}
                        {/*        classes={{*/}
                        {/*            error:   classes.error,*/}
                        {/*            focused: classes.labelFocused,*/}
                        {/*            root:    classes.label*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.facilitiesContact) && Boolean(formik.touched.facilitiesContact)}*/}
                        {/*    >*/}
                        {/*        Facilities Contact*/}
                        {/*    </InputLabel>*/}
                        {/*    <Select*/}
                        {/*        value={formik.values.facilitiesContact}*/}
                        {/*        id="facilitiesContact"*/}
                        {/*        onChange={formik.handleChange}*/}
                        {/*        name="facilitiesContact"*/}
                        {/*        classes={{*/}
                        {/*            root: classes.input*/}
                        {/*        }}*/}
                        {/*        inputProps={{*/}
                        {/*            onBlur: formik.handleBlur*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.facilitiesContact) && Boolean(formik.touched.facilitiesContact)}*/}
                        {/*    >*/}
                        {/*        {*/}
                        {/*            contacts.map(type =>*/}
                        {/*                <MenuItem*/}
                        {/*                    key={type.id}*/}
                        {/*                    value={type.id}*/}
                        {/*                >*/}
                        {/*                    {type.name}*/}
                        {/*                </MenuItem>*/}
                        {/*            )*/}
                        {/*        }*/}
                        {/*    </Select>*/}
                        {/*    {formik.touched.facilitiesContact &&*/}
                        {/*     formik.errors.facilitiesContact && <FormHelperText className={classes.error}>{formik.errors.facilitiesContact}</FormHelperText>}*/}
                        {/*</FormControl>*/}
                        {/*<FormControl className={classes.inputWrapper}>*/}
                        {/*    <InputLabel*/}
                        {/*        htmlFor="salesContact"*/}
                        {/*        classes={{*/}
                        {/*            error:   classes.error,*/}
                        {/*            focused: classes.labelFocused,*/}
                        {/*            root:    classes.label*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.salesContact) && Boolean(formik.touched.salesContact)}*/}
                        {/*    >*/}
                        {/*        Sales Contact*/}
                        {/*    </InputLabel>*/}
                        {/*    <Select*/}
                        {/*        value={formik.values.salesContact}*/}
                        {/*        id="salesContact"*/}
                        {/*        onChange={formik.handleChange}*/}
                        {/*        name="salesContact"*/}
                        {/*        classes={{*/}
                        {/*            root: classes.input*/}
                        {/*        }}*/}
                        {/*        inputProps={{*/}
                        {/*            onBlur: formik.handleBlur*/}
                        {/*        }}*/}
                        {/*        error={Boolean(formik.errors.salesContact) && Boolean(formik.touched.salesContact)}*/}
                        {/*    >*/}
                        {/*        {*/}
                        {/*            contacts.map(type =>*/}
                        {/*                <MenuItem*/}
                        {/*                    key={type.id}*/}
                        {/*                    value={type.id}*/}
                        {/*                >*/}
                        {/*                    {type.name}*/}
                        {/*                </MenuItem>*/}
                        {/*            )*/}
                        {/*        }*/}
                        {/*    </Select>*/}
                        {/*    {formik.touched.salesContact &&*/}
                        {/*     formik.errors.salesContact && <FormHelperText className={classes.error}>{formik.errors.salesContact}</FormHelperText>}*/}
                        {/*</FormControl>*/}
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <PlacesAutocomplete
                            value={formik.values.addressSearch}
                            onChange={address => formik.setFieldValue('addressSearch', address)}
                            onSelect={(handleGeocodeAddress)}
                        >
                            {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                <React.Fragment>
                                    <FormControl className={classes.inputWrapper}>
                                        <InputLabel
                                            htmlFor="addressSearch"
                                            classes={{
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        >
                                            Search for Address
                                        </InputLabel>
                                        <Input
                                            {...getInputProps({
                                                name: 'addressSearch',
                                                id:   'addressSearch'
                                            })}
                                            classes={{
                                                error:     classes.cssErrorUnderline,
                                                underline: classes.cssUnderline,
                                                root:      classes.input
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        />
                                        {formik.touched.addressSearch &&
                                         formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                                    </FormControl>
                                    {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                        <List>
                                            {suggestions.map((suggestion, index) => {
                                                return (
                                                    <div key={index}>
                                                        <ListItem
                                                            button
                                                            className={classes.autoCompleteListItem}
                                                            {...getSuggestionItemProps(suggestion)}
                                                        >
                                                            <ListItemText primary={suggestion.description} />
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                        </List>
                                        <GoogleLogo />
                                    </Paper>}
                                </React.Fragment>
                            )}
                        </PlacesAutocomplete>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="address1"
                                name="address1"
                                type="text"
                                value={address1}
                                label="Address"
                                disabled
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="city"
                                name="city"
                                type="text"
                                value={city}
                                label="City"
                                disabled
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={`${classes.inputWrapper} ${classes.state}`}>
                                <TextField
                                    id="state"
                                    name="state"
                                    type="text"
                                    value={state}
                                    label="State"
                                    disabled
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormControl className={`${classes.inputWrapper} ${classes.zip}`}>
                                <TextField
                                    id="zip"
                                    name="zip"
                                    type="text"
                                    value={zip}
                                    label="Zip Code"
                                    disabled
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                        </div>
                        <FormControl className={`${classes.inputWrapper}`}>
                            <TimeZoneAutoSelect
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.timeZone}
                                error={Boolean(formik.errors.timeZone) && Boolean(formik.touched.timeZone)}
                                helperText={Boolean(formik.touched.timeZone) && formik.errors.timeZone}
                                label="Time Zone"
                                placeholder="Time Zone"
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateSiteModal.defaultProps = {
    buildingTypes: [],
    clientNodes:   [],
    fuelTypes:     [],
    selectedNode:  {}
};


CreateSiteModal.propTypes = {
    buildingTypes: array,
    clientNodes:   array,
    close:         func.isRequired,
    fuelTypes:     array,
    open:          bool.isRequired,
    selectedNode:  object,
    title:         string.isRequired
};

const mapStateToProps = state => {
    return {
        buildingTypes:  state.architect.buildingTypes,
        clientNodes:    state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        contacts:       state.architect.contacts,
        errors:         state.messages.errors,
        fuelTypes:      state.architect.fuelTypes,
        isLoading:      state.ui.isLoading,
        selectedNode:   state.architect.selectedNode,
        selectedClient: state.client.selectedClient
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSiteModal);
